import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, getODataRows,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon, unwrapLookupItems, buildSelectFields,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/payments';
const PAYMENT_TYPE_API = '/api/paymenttypes';
const INVOICE_API = '/api/invoices';

const STATUS_OPTIONS = [
  { value: 0, label: 'Beklemede' },
  { value: 1, label: 'Tamamlandi' },
  { value: 2, label: 'Iptal' },
];
const REQUEST_STATUS_OPTIONS = [
  { value: 0, label: 'Yok' },
  { value: 1, label: 'Talep Gonderildi' },
  { value: 2, label: 'Onaylandi' },
  { value: 3, label: 'Reddedildi' },
];

const COLUMN_FILTER_META = {
  textId: { type: 'text', odataField: 'TextId' },
  bankName: { type: 'text', odataField: 'BankName' },
  receiptNo: { type: 'text', odataField: 'ReceiptNo' },
  amount: { type: 'number', odataField: 'Amount' },
  transactionRequestStatus: { type: 'select', odataField: 'TransactionRequestStatus', options: REQUEST_STATUS_OPTIONS },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
};
// Sadece string alanlar; int/guid'e contains uygulanmaz.
const SEARCH_FIELDS = ['TextId', 'BankName', 'ReceiptNo'];

function toInt(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function formatMoney(value) {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '-';
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
}

function statusLabel(value) {
  return STATUS_OPTIONS.find((o) => o.value === toInt(value))?.label || 'Beklemede';
}

function requestStatusLabel(value) {
  return REQUEST_STATUS_OPTIONS.find((o) => o.value === toInt(value))?.label || 'Yok';
}

function lookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || '';
  const desc = item.description || item.Description || item.name || item.Name || item.title || item.Title || '';
  return desc ? `${code} - ${desc}` : code;
}

function invoiceLabelOf(inv) {
  if (!inv) return '';
  const code = inv.textId || inv.TextId || '';
  const net = inv.netAmount ?? inv.NetAmount;
  return net != null ? `${code} - ${formatMoney(net)}` : code;
}

export function PaymentTrackingPage() {
  usePageMeta('Odeme Takip', 'Odeme kayitlarini fatura ile bagli sekilde izleyin, filtreleyin ve yonetin.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [paymentTypes, setPaymentTypes] = useState([]); // küçük lookup → tümü /Select
  const [invoiceLabels, setInvoiceLabels] = useState({}); // id -> { label, netAmount, dueDate } (lazy)

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // $select: yalnız görünür kolonların OData alanları + satır render'ında (kolon-dışı) okunan
  // sabit alanlar çekilir. Sıralanan alan $orderby ile sunucuda uygulanır ($select'ten bağımsız).
  // Grup/kanban/chart yok → needsFullData daima false. always: fatura/ödeme tipi lookup id'leri,
  // ödeme tarihi ve silme modalindeki TextId kolon meta'sında (görünür) olmayabilir.
  const selectFields = useMemo(
    () => buildSelectFields({
      columnFilterMeta: COLUMN_FILTER_META,
      columnVisibility,
      always: ['Id', 'InvoiceId', 'PaymentTypeId', 'PaymentDate', 'TextId'],
    }),
    [columnVisibility],
  );

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'PaymentDate',
    defaultSortDir: 'desc',
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  // Küçük lookup: ödeme tipleri (tümü)
  useEffect(() => {
    fetch(`${PAYMENT_TYPE_API}/Select`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const items = unwrapLookupItems(data)
          .filter((item) => item?.isActive !== false)
          .sort((a, b) => (a.textId || '').localeCompare(b.textId || '', 'tr'));
        setPaymentTypes(items);
      })
      .catch(() => {});
  }, []);

  const paymentTypeName = useMemo(() => {
    const map = new Map(paymentTypes.map((p) => [p.id, lookupLabel(p)]));
    return (id) => (id ? map.get(id) || '' : '');
  }, [paymentTypes]);
  const invoiceName = useCallback((id) => (id ? (invoiceLabels[id]?.label || '…') : ''), [invoiceLabels]);

  // Büyük lookup: fatura etiketlerini id bazında lazy çek.
  const invFetchRef = useRef(new Set());
  const ensureInvoiceLabels = useCallback(async (ids) => {
    const need = [...new Set(ids.filter((id) => id && invoiceLabels[id] === undefined && !invFetchRef.current.has(id)))];
    if (!need.length) return;
    need.forEach((id) => invFetchRef.current.add(id));
    try {
      for (let i = 0; i < need.length; i += 50) {
        const chunk = need.slice(i, i + 50);
        const filter = chunk.map((id) => `Id eq ${id}`).join(' or ');
        const url = `${INVOICE_API}?$top=500&$filter=${encodeURIComponent(`(${filter})`)}`;
        const res = await fetch(url, { headers: authHeaders() });
        const found = {};
        if (res.ok) {
          const data = await res.json();
          getODataRows(data).forEach((inv) => {
            found[inv.id] = { label: invoiceLabelOf(inv), netAmount: inv.netAmount ?? 0, dueDate: inv.dueDate ?? null, textId: inv.textId || '' };
          });
        }
        setInvoiceLabels((prev) => { const next = { ...prev }; chunk.forEach((id) => { next[id] = found[id] || { label: '-', netAmount: 0, dueDate: null, textId: '' }; }); return next; });
      }
    } finally {
      need.forEach((id) => invFetchRef.current.delete(id));
    }
  }, [invoiceLabels]);

  useEffect(() => {
    if (rows.length) ensureInvoiceLabels(rows.map((r) => r.invoiceId).filter(Boolean));
  }, [rows, ensureInvoiceLabels]);

  const openNew = () => navigate('/payment/new');
  const openDetail = (row = selected) => {
    if (!row) return;
    navigate(`/payment/${row.id}`, { state: { row } });
  };
  const openEditRow = (row = selected) => {
    if (!row) return;
    navigate(`/payment/${row.id}?edit=1`, { state: { row } });
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ id: selected.id }),
      });
      if (!res.ok) throw new Error('Odeme kaydi silinemedi');
      showToast('success', 'Odeme kaydi silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme islemi sirasinda hata olustu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('textId', {
      header: 'Odeme No', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.display({
      id: 'invoice', header: 'Fatura No', enableColumnFilter: false,
      cell: ({ row }) => { const lbl = invoiceName(row.original.invoiceId); return lbl ? <span className="c360-erp-chip">{lbl}</span> : '-'; },
    }),
    columnHelper.accessor('bankName', { header: 'Banka', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('paymentDate', {
      header: 'Odeme Tarihi', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.display({
      id: 'paymentType', header: 'Odeme Tipi', enableColumnFilter: false,
      cell: ({ row }) => paymentTypeName(row.original.paymentTypeId) || '-',
    }),
    columnHelper.accessor('amount', {
      header: 'Odeme Tutari', meta: { filterType: 'number' },
      cell: (info) => formatMoney(info.getValue()),
    }),
    columnHelper.accessor('receiptNo', { header: 'Makbuz', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('transactionRequestStatus', {
      header: 'Islem Talebi', meta: { filterType: 'select' },
      cell: (info) => requestStatusLabel(info.getValue()),
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => {
        const v = toInt(info.getValue());
        const tone = v === 1 ? 'ok' : v === 2 ? 'bad' : 'neutral';
        return <StatusBadge tone={tone}>{statusLabel(v)}</StatusBadge>;
      },
    }),
    columnHelper.display({
      id: 'actions', header: 'Islemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [invoiceName, paymentTypeName, canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('PaymentDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'textId', headerName: 'Odeme No' },
        { headerName: 'Fatura No', valueGetter: ({ row }) => invoiceName(row.invoiceId) },
        { field: 'bankName', headerName: 'Banka' },
        { headerName: 'Odeme Tarihi', valueGetter: ({ row }) => formatDate(row.paymentDate) },
        { headerName: 'Odeme Tipi', valueGetter: ({ row }) => paymentTypeName(row.paymentTypeId) },
        { headerName: 'Odeme Tutari', valueGetter: ({ row }) => formatMoney(row.amount) },
        { field: 'receiptNo', headerName: 'Makbuz' },
        { headerName: 'Islem Talebi', valueGetter: ({ row }) => requestStatusLabel(row.transactionRequestStatus) },
        { headerName: 'Durum', valueGetter: ({ row }) => statusLabel(row.status) },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `odeme-takip_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Odeme no, banka veya makbuz ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Odeme Takip"
        subtitle="Odeme kayitlarini fatura ile bagli sekilde izleyin, filtreleyin ve yonetin."
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Odeme</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayi</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>"{selected.textId}"</strong> odeme kaydini silmek istediginize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>Iptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
